/**
 * HTTP service for project documents (PX-W6.3, TASK-EDD04F2F).
 *
 * ⚠ AC-4 wording deviation, verified against `conf/routes` +
 * `ProjectFileController`: the px backend exposes NO signed-URL upload route.
 * The implemented flow is the one that exists — a MULTIPART `POST …/files`
 * with field name `file`, stored via DocumentStorage (S3-compatible primary,
 * GridFS dev fallback). Spec OQ-1 carries the wording question for Agon.
 *
 * Downloads go through the shared transport as a blob so the Bearer rides
 * the interceptor (a bare <a href> could never authenticate).
 */

import { apiClient } from '@/services'
import type { ProjectFileRow } from '../types'

const base = (orgId: string, projectId: string): string => `/${orgId}/projects/${projectId}/files`

export const projectFileService = {
  /** The project's documents, newest first (`GET …/files`). */
  async list(orgId: string, projectId: string): Promise<ProjectFileRow[]> {
    return apiClient.get<ProjectFileRow[]>(base(orgId, projectId))
  },

  /**
   * Upload one document (`POST …/files`, multipart field `file`, 201 → the
   * created row). Synchronous Tika extraction server-side.
   */
  async upload(orgId: string, projectId: string, file: File): Promise<ProjectFileRow> {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post<ProjectFileRow>(base(orgId, projectId), formData, {
      // The xelerate-ai `attachmentService.http.ts` shape (the plan's named
      // reference): axios sees the FormData body and completes this header
      // with the generated boundary parameter.
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  /** One document row (`GET …/files/:fileId`). */
  async get(orgId: string, projectId: string, fileId: string): Promise<ProjectFileRow> {
    return apiClient.get<ProjectFileRow>(`${base(orgId, projectId)}/${fileId}`)
  },

  /**
   * The authenticated content path for `GET …/files/:fileId/content` — the
   * panel fetches it through `apiClient` as a BLOB (auth header rides the
   * interceptor) and hands the object-URL to the browser's download flow.
   */
  async download(orgId: string, projectId: string, fileId: string): Promise<Blob> {
    return apiClient.get<Blob>(`${base(orgId, projectId)}/${fileId}/content`, {
      responseType: 'blob'
    })
  },

  /** Remove a document (`DELETE …/files/:fileId`). */
  async remove(orgId: string, projectId: string, fileId: string): Promise<void> {
    return apiClient.delete<void>(`${base(orgId, projectId)}/${fileId}`)
  }
}
